import { useState } from 'react'
import { AlertTriangle, Plus } from 'lucide-react'
import { Link } from 'react-router'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useAppraisalCycles, useAppraisalMutations, useAppraisalTemplates } from '../api'
import { CYCLE_STATUS_LABELS } from '../types'
import type { CycleStatus } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const STATUS_TONE: Record<CycleStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  OPEN: 'info',
  IN_REVIEW: 'warning',
  CALIBRATION: 'warning',
  CLOSED: 'success',
  CANCELLED: 'neutral',
}

/** Appraisal cycles: a period, a template, and everybody being appraised in it. */
export function AppraisalCyclesPage() {
  useBreadcrumb()
  const cycles = useAppraisalCycles()
  const templates = useAppraisalTemplates()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { saveCycle, openCycle } = useAppraisalMutations()

  const [draft, setDraft] = useState<{
    legalEntityId: string; templateId: string; code: string; name: string
    periodStart: string; periodEnd: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const usableTemplates = templates.data?.filter((template) => !template.weightWarning) ?? []

  const act = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setError(null)
      try {
        await work()
      } catch (caught) {
        setError(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Appraisal cycles"
        subtitle="Periods being appraised, and how far each has got."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                legalEntityId: entities.data?.[0]?.id ?? '',
                templateId: usableTemplates[0]?.id ?? '',
                code: '', name: '', periodStart: '', periodEnd: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New cycle
          </Button>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <Card>
        {cycles.isPending && <Skeleton className="m-4 h-24" />}
        {cycles.isError && <ErrorState error={cycles.error} onRetry={() => void cycles.refetch()} />}
        {cycles.isSuccess && cycles.data.length === 0 && (
          <EmptyState
            title="No cycle has been run"
            description="A cycle takes a template, a period and a group of people, and creates one appraisal each with the reviewer passes the template expects."
          />
        )}
        {cycles.isSuccess && cycles.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Period</th>
                  <th className="p-3 text-start font-medium">Template</th>
                  <th className="p-3 text-end font-medium">Appraisals</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {cycles.data.map((cycle) => (
                  <tr key={cycle.id} className="border-b border-slate-100">
                    <td className="p-3 font-mono">{cycle.code}</td>
                    <td className="p-3">
                      <Link
                        className="underline underline-offset-2"
                        to={`/performance/cycles/${cycle.id}`}
                      >
                        {cycle.name}
                      </Link>
                    </td>
                    <td className="p-3 text-slate-600">
                      {cycle.periodStart} → {cycle.periodEnd}
                    </td>
                    <td className="p-3 text-slate-600">{cycle.templateName ?? '—'}</td>
                    <td className="p-3 text-end">
                      {cycle.completedCount} of {cycle.appraisalCount}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={cycle.status}
                        tone={STATUS_TONE[cycle.status as CycleStatus] ?? 'neutral'}
                        label={CYCLE_STATUS_LABELS[cycle.status as CycleStatus] ?? cycle.status}
                      />
                    </td>
                    <td className="p-3 text-end">
                      {cycle.status === 'DRAFT' && (
                        <Button
                          type="button"
                          onClick={() => act(
                            () => openCycle.mutateAsync({ id: cycle.id }),
                            'That cycle could not be opened')}
                        >
                          Open it
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {templates.isSuccess && usableTemplates.length === 0 && templates.data.length > 0 && (
        <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          No template's sections total 100%, so no cycle can open yet. A score that cannot be
          reproduced by hand from the ratings is one nobody can dispute honestly, so the
          weights have to add up before anybody is appraised.
        </p>
      )}

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title="New appraisal cycle"
        description="Opening it creates one appraisal per person, with the manager as of the period's end recorded on each."
        submitLabel="Save as draft"
        loading={saveCycle.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          act(async () => {
            await saveCycle.mutateAsync({
              legalEntityId: draft.legalEntityId,
              templateId: draft.templateId,
              code: draft.code,
              name: draft.name,
              periodStart: draft.periodStart,
              periodEnd: draft.periodEnd,
            })
            setDraft(null)
          }, 'That cycle could not be saved')
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="cycle-entity">Legal entity</Label>
              <Select
                id="cycle-entity"
                className="w-full"
                value={draft.legalEntityId}
                onChange={(event) => setDraft({ ...draft, legalEntityId: event.target.value })}
              >
                <option value="">Choose an entity…</option>
                {entities.data?.map((entity) => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="cycle-template">Template</Label>
              <Select
                id="cycle-template"
                className="w-full"
                value={draft.templateId}
                onChange={(event) => setDraft({ ...draft, templateId: event.target.value })}
              >
                <option value="">Choose a template…</option>
                {usableTemplates.map((template) => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                Only templates whose weights total 100% are offered.
              </p>
            </div>
            <div>
              <Label htmlFor="cycle-code">Code</Label>
              <Input
                id="cycle-code" value={draft.code}
                onChange={(event) =>
                  setDraft({ ...draft, code: event.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label htmlFor="cycle-name">Name</Label>
              <Input
                id="cycle-name" value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="cycle-from">Period from</Label>
              <DatePicker
                id="cycle-from" value={draft.periodStart}
                onChange={(event) => setDraft({ ...draft, periodStart: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="cycle-to">Period to</Label>
              <DatePicker
                id="cycle-to" value={draft.periodEnd}
                onChange={(event) => setDraft({ ...draft, periodEnd: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
